/**
 * Ideas endpoints — every route under /api/v1/ideas, all URLs with a trailing
 * slash to avoid FastAPI 307 redirects.
 *
 * A content write names the revision it was made against: `ifMatch` is
 * required on both, and a superseded revision answers 412.
 */

import type {
  IdeaAskingForInput,
  IdeaBacklinksResponse,
  IdeaBacklinkTargetKind,
  IdeaContentUpdate,
  IdeaCreate,
  IdeaDecisionListResponse,
  IdeaDecisionResponse,
  IdeaDecisionVerdict,
  IdeaEntryCreate,
  IdeaEntryListParams,
  IdeaEntryListResponse,
  IdeaEntryResolve,
  IdeaEntryResponse,
  IdeaEntryUpdate,
  IdeaInputRequestCreate,
  IdeaInputRequestResponse,
  IdeaLeadHandOver,
  IdeaLinksResponse,
  IdeaListFilters,
  IdeaListResponse,
  IdeaMentionCandidateListResponse,
  IdeaPageParams,
  IdeaParticipantListResponse,
  IdeaResponse,
  IdeaRevisionListResponse,
  IdeaRevisionResponse,
  IdeaStateTransition,
  IdeaTemplatesResponse,
  IdeaTimelineResponse,
  IdeaWriteUpEditResponse,
  IdeaWriteUpEdits,
  IdeaWriteUpWindowParams,
  IdeaWriteUpWindowResponse,
  WorkItemLinkCreate,
  WorkItemLinkView,
} from '@/types/idea'
import { apiClient } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'

export const ideas = {
  list: async (filters?: IdeaListFilters): Promise<IdeaListResponse> => {
    const response = await apiClient.get<IdeaListResponse>('/api/v1/ideas/', {
      params: filters,
      paramsSerializer: repeatedKeyParamsSerializer,
    })
    return response.data
  },

  create: async (data: IdeaCreate): Promise<IdeaResponse> => {
    const response = await apiClient.post<IdeaResponse>('/api/v1/ideas/', data)
    return response.data
  },

  templates: async (): Promise<IdeaTemplatesResponse> => {
    const response = await apiClient.get<IdeaTemplatesResponse>(
      '/api/v1/ideas/templates/'
    )
    return response.data
  },

  getByNumber: async (n: number): Promise<IdeaResponse> => {
    const response = await apiClient.get<IdeaResponse>(
      `/api/v1/ideas/by-number/${n}/`
    )
    return response.data
  },

  get: async (id: string): Promise<IdeaResponse> => {
    const response = await apiClient.get<IdeaResponse>(`/api/v1/ideas/${id}/`)
    return response.data
  },

  update: async (
    id: string,
    data: IdeaContentUpdate,
    ifMatch: number
  ): Promise<IdeaResponse> => {
    const response = await apiClient.put<IdeaResponse>(
      `/api/v1/ideas/${id}/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/ideas/${id}/`)
  },

  writeUpWindow: async (
    id: string,
    params: IdeaWriteUpWindowParams = {}
  ): Promise<IdeaWriteUpWindowResponse> => {
    const response = await apiClient.get<IdeaWriteUpWindowResponse>(
      `/api/v1/ideas/${id}/write-up/`,
      { params }
    )
    return response.data
  },

  editWriteUp: async (
    id: string,
    data: IdeaWriteUpEdits,
    ifMatch: number
  ): Promise<IdeaWriteUpEditResponse> => {
    const response = await apiClient.patch<IdeaWriteUpEditResponse>(
      `/api/v1/ideas/${id}/write-up/edits/`,
      data,
      ifMatchHeaders(ifMatch)
    )
    return response.data
  },

  listRevisions: async (
    id: string,
    params: IdeaPageParams = {}
  ): Promise<IdeaRevisionListResponse> => {
    const response = await apiClient.get<IdeaRevisionListResponse>(
      `/api/v1/ideas/${id}/revisions/`,
      { params }
    )
    return response.data
  },

  getRevision: async (
    id: string,
    revisionNumber: number
  ): Promise<IdeaRevisionResponse> => {
    const response = await apiClient.get<IdeaRevisionResponse>(
      `/api/v1/ideas/${id}/revisions/${revisionNumber}/`
    )
    return response.data
  },

  timeline: async (id: string): Promise<IdeaTimelineResponse> => {
    const response = await apiClient.get<IdeaTimelineResponse>(
      `/api/v1/ideas/${id}/timeline/`
    )
    return response.data
  },

  claim: async (id: string): Promise<IdeaResponse> => {
    const response = await apiClient.post<IdeaResponse>(
      `/api/v1/ideas/${id}/claim/`,
      {}
    )
    return response.data
  },

  handOver: async (id: string, data: IdeaLeadHandOver): Promise<IdeaResponse> => {
    const response = await apiClient.put<IdeaResponse>(
      `/api/v1/ideas/${id}/lead/`,
      data
    )
    return response.data
  },

  transitionState: async (
    id: string,
    data: IdeaStateTransition
  ): Promise<IdeaResponse> => {
    const response = await apiClient.put<IdeaResponse>(
      `/api/v1/ideas/${id}/state/`,
      data
    )
    return response.data
  },

  setAskingForInput: async (
    id: string,
    data: IdeaAskingForInput
  ): Promise<IdeaResponse> => {
    const response = await apiClient.put<IdeaResponse>(
      `/api/v1/ideas/${id}/asking-for-input/`,
      data
    )
    return response.data
  },

  listEntries: async (
    id: string,
    params: IdeaEntryListParams = {}
  ): Promise<IdeaEntryListResponse> => {
    const response = await apiClient.get<IdeaEntryListResponse>(
      `/api/v1/ideas/${id}/entries/`,
      { params }
    )
    return response.data
  },

  postEntry: async (
    id: string,
    data: IdeaEntryCreate
  ): Promise<IdeaEntryResponse> => {
    const response = await apiClient.post<IdeaEntryResponse>(
      `/api/v1/ideas/${id}/entries/`,
      data
    )
    return response.data
  },

  editEntry: async (
    id: string,
    entryNumber: number,
    data: IdeaEntryUpdate
  ): Promise<IdeaEntryResponse> => {
    const response = await apiClient.patch<IdeaEntryResponse>(
      `/api/v1/ideas/${id}/entries/${entryNumber}/`,
      data
    )
    return response.data
  },

  resolveEntry: async (
    id: string,
    entryNumber: number,
    data: IdeaEntryResolve
  ): Promise<IdeaEntryResponse> => {
    const response = await apiClient.post<IdeaEntryResponse>(
      `/api/v1/ideas/${id}/entries/${entryNumber}/resolve/`,
      data
    )
    return response.data
  },

  listDecisions: async (id: string): Promise<IdeaDecisionListResponse> => {
    const response = await apiClient.get<IdeaDecisionListResponse>(
      `/api/v1/ideas/${id}/decisions/`
    )
    return response.data
  },

  acceptDecision: async (
    id: string,
    entryNumber: number,
    data: IdeaDecisionVerdict
  ): Promise<IdeaDecisionResponse> => {
    const response = await apiClient.post<IdeaDecisionResponse>(
      `/api/v1/ideas/${id}/decisions/${entryNumber}/accept/`,
      data
    )
    return response.data
  },

  rejectDecision: async (
    id: string,
    entryNumber: number,
    data: IdeaDecisionVerdict
  ): Promise<IdeaDecisionResponse> => {
    const response = await apiClient.post<IdeaDecisionResponse>(
      `/api/v1/ideas/${id}/decisions/${entryNumber}/reject/`,
      data
    )
    return response.data
  },

  listParticipants: async (id: string): Promise<IdeaParticipantListResponse> => {
    const response = await apiClient.get<IdeaParticipantListResponse>(
      `/api/v1/ideas/${id}/participants/`
    )
    return response.data
  },

  follow: async (id: string): Promise<IdeaResponse> => {
    const response = await apiClient.put<IdeaResponse>(
      `/api/v1/ideas/${id}/follow/`
    )
    return response.data
  },

  unfollow: async (id: string): Promise<IdeaResponse> => {
    const response = await apiClient.delete<IdeaResponse>(
      `/api/v1/ideas/${id}/follow/`
    )
    return response.data
  },

  markSeen: async (id: string): Promise<void> => {
    await apiClient.put(`/api/v1/ideas/${id}/seen/`)
  },

  mentionCandidates: async (
    id: string,
    q: string
  ): Promise<IdeaMentionCandidateListResponse> => {
    const response = await apiClient.get<IdeaMentionCandidateListResponse>(
      `/api/v1/ideas/${id}/mention-candidates/`,
      { params: { q } }
    )
    return response.data
  },

  requestInput: async (
    id: string,
    data: IdeaInputRequestCreate
  ): Promise<IdeaInputRequestResponse> => {
    const response = await apiClient.post<IdeaInputRequestResponse>(
      `/api/v1/ideas/${id}/input-requests/`,
      data
    )
    return response.data
  },

  withdrawInputRequest: async (
    id: string,
    requestId: string
  ): Promise<IdeaInputRequestResponse> => {
    const response = await apiClient.delete<IdeaInputRequestResponse>(
      `/api/v1/ideas/${id}/input-requests/${requestId}/`
    )
    return response.data
  },

  /** The Ideas linking to one TODO or PRD; those the caller may not read are only counted. */
  backlinks: async (
    targetKind: IdeaBacklinkTargetKind,
    targetId: string
  ): Promise<IdeaBacklinksResponse> => {
    const response = await apiClient.get<IdeaBacklinksResponse>(
      '/api/v1/ideas/links/',
      { params: { target_kind: targetKind, target_id: targetId } }
    )
    return response.data
  },

  listLinks: async (id: string): Promise<IdeaLinksResponse> => {
    const response = await apiClient.get<IdeaLinksResponse>(
      `/api/v1/ideas/${id}/links/`
    )
    return response.data
  },

  /** A repeat of an existing link answers that link unchanged. */
  linkWork: async (
    id: string,
    data: WorkItemLinkCreate
  ): Promise<WorkItemLinkView> => {
    const response = await apiClient.post<WorkItemLinkView>(
      `/api/v1/ideas/${id}/links/`,
      data
    )
    return response.data
  },
}
